/**
 * Feb 2026 · Task recurrence editor — Google-Calendar-style.
 * Emits a `{frequency, interval, weekdays, occurrences, until_date}` rule
 * plus a `mode` ('on_completion' | 'on_schedule') as separate fields on
 * the parent form.
 *
 * Usage:
 *   <RecurrenceEditor value={form.recurrence} mode={form.recurrence_mode}
 *     onChange={(rule, mode) => setForm({...form, recurrence: rule, recurrence_mode: mode})}
 *     testid="task-recurrence" />
 */
import { useMemo } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Repeat, X } from "lucide-react";

const WEEKDAYS = [
  ["MO", "Mon"], ["TU", "Tue"], ["WE", "Wed"], ["TH", "Thu"],
  ["FR", "Fri"], ["SA", "Sat"], ["SU", "Sun"],
];

function describeRule(rule) {
  if (!rule) return "One-off — does not repeat.";
  const { frequency, interval = 1, weekdays = [], occurrences = 12, until_date } = rule;
  const iv = interval > 1 ? `every ${interval} ` : "";
  let base = "";
  if (frequency === "daily") base = `Repeats ${iv}day${interval > 1 ? "s" : ""}`;
  else if (frequency === "weekly") {
    if (weekdays.length) {
      const days = weekdays.join(", ");
      base = `Repeats ${iv}week${interval > 1 ? "s" : ""} on ${days}`;
    } else base = `Repeats ${iv}week${interval > 1 ? "s" : ""}`;
  }
  else if (frequency === "monthly") base = `Repeats ${iv}month${interval > 1 ? "s" : ""}`;
  else if (frequency === "yearly") base = `Repeats ${iv}year${interval > 1 ? "s" : ""}`;
  else if (frequency === "custom") base = `Repeats every ${interval} day${interval > 1 ? "s" : ""}`;
  else base = "Repeats";
  const tail = until_date ? ` until ${until_date}` : ` — max ${occurrences} occurrences`;
  return base + tail + ".";
}

export default function RecurrenceEditor({ value, mode = "on_completion", onChange,
                                                startDate, testid = "task-recurrence" }) {
  const enabled = !!value;
  const rule = value || {};
  const setRule = (patch) => onChange({ ...rule, ...patch }, mode);
  const setMode = (m) => onChange(rule, m);
  const description = useMemo(() => describeRule(enabled ? rule : null), [rule, enabled]);

  const toggleWeekday = (code) => {
    const cur = new Set(rule.weekdays || []);
    if (cur.has(code)) cur.delete(code); else cur.add(code);
    setRule({ weekdays: Array.from(cur) });
  };

  return (
    <div className="border border-border p-3 bg-muted/20" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <label className="inline-flex items-center gap-2 text-xs font-semibold cursor-pointer">
          <input type="checkbox" checked={enabled}
                  onChange={(e) => onChange(e.target.checked
                    ? { frequency: "weekly", interval: 1, occurrences: 12 }
                    : null, mode)}
                  data-testid={`${testid}-toggle`} />
          <Repeat className="w-3 h-3 text-primary" />
          Repeat this task
        </label>
        {enabled && (
          <button type="button" onClick={() => onChange(null, mode)}
                    className="ml-auto text-[10px] text-muted-foreground hover:text-rose-600 inline-flex items-center gap-1"
                    data-testid={`${testid}-clear`}>
            <X className="w-3 h-3" /> Clear
          </button>
        )}
      </div>
      {!enabled ? (
        <p className="text-[10px] text-muted-foreground italic">
          Turn on to spawn future copies of this task automatically (e.g. monthly website maintenance).
        </p>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Frequency</Label>
              <Select value={rule.frequency || "weekly"} onValueChange={(v) => setRule({ frequency: v })}>
                <SelectTrigger className="h-9 text-xs" data-testid={`${testid}-frequency`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                  <SelectItem value="custom">Custom (every N days)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Every</Label>
              <div className="flex items-center gap-1.5">
                <Input type="number" min="1" max="52"
                        value={rule.interval || 1}
                        onChange={(e) => setRule({ interval: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                        className="h-9 text-xs w-20"
                        data-testid={`${testid}-interval`} />
                <span className="text-[10px] text-muted-foreground">
                  {rule.frequency === "daily" || rule.frequency === "custom" ? "day(s)"
                   : rule.frequency === "weekly" ? "week(s)"
                   : rule.frequency === "monthly" ? "month(s)"
                   : "year(s)"}
                </span>
              </div>
            </div>
          </div>

          {rule.frequency === "weekly" && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">On weekdays (leave empty = same weekday as start)</Label>
              <div className="flex gap-1 flex-wrap" data-testid={`${testid}-weekdays`}>
                {WEEKDAYS.map(([code, label]) => {
                  const active = (rule.weekdays || []).includes(code);
                  return (
                    <button key={code} type="button" onClick={() => toggleWeekday(code)}
                             className={`px-2 py-1 text-[10px] border ${active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background hover:bg-muted"}`}
                             data-testid={`${testid}-day-${code}`}>
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Ends after</Label>
              <div className="flex items-center gap-1.5">
                <Input type="number" min="1" max="104"
                        value={rule.occurrences || 12}
                        onChange={(e) => setRule({ occurrences: Math.max(1, parseInt(e.target.value, 10) || 12) })}
                        className="h-9 text-xs w-20"
                        data-testid={`${testid}-occurrences`} />
                <span className="text-[10px] text-muted-foreground">occurrences</span>
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Or until</Label>
              <Input type="date" value={rule.until_date || ""}
                      onChange={(e) => setRule({ until_date: e.target.value || null })}
                      className="h-9 text-xs"
                      data-testid={`${testid}-until`} />
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">How to schedule the next task</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="h-9 text-xs" data-testid={`${testid}-mode`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="on_completion">On completion — spawn the next task when this one is done (default)</SelectItem>
                <SelectItem value="on_schedule">On schedule — pre-create all future occurrences on their due dates</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="text-[11px] italic text-muted-foreground border-l-2 border-primary/50 pl-2">
            {description}
          </div>
        </div>
      )}
    </div>
  );
}
